import type { Procedure as CatalogProcedure } from "@/types";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";

import {
  Button,
  Input,
  Label,
  MoneyInput,
  ScrollArea,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  MultiSelect,
  Option,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui";
import {
  BudgetRequestProcedureValidationSchema,
  BudgetValidationSchema,
} from "@/shared";
import {
  useCreateBudgetMutation,
  useFetchEmployeesNoPaginateQuery,
  useFetchPatientsQuery,
  useFetchProceduresQuery,
  useToast,
} from "@/hooks";

import { ArrowDown, Pencil, Trash } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { DisplayError, formatValue } from "@/utils";
import { useDraftStore } from "@/stores";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PatientsResponse, ProcedureResponse } from "@/types";
import { httpRequest } from "@/lib";

type Procedure = {
  procedureName: string;
  employeeName: string;
  id: string;
  quantity: number;
  value: number;
  employee_id: string;
};

type DraftProps = {
  patient_id: string;
  observations: string;
  voucherCode: string;
  procedures: Procedure[];
};

type FormCreateBudgetProps = {
  handleClose: () => void;
  patientId?: string;
};

export function FormCreateBudget({ handleClose, patientId }: FormCreateBudgetProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const queryClient = useQueryClient();
  const searchedProcedures = useRef(new Map<string, CatalogProcedure>());
  const searchedPatients = useRef(new Map<string, Option>());

  const { getState } = useDraftStore<DraftProps>({ key: "budget" });
  const { draft, setDraft, clearDraft } = getState();

  const { mutateAsync, isLoading } = useCreateBudgetMutation();

  const { data: proceduresData, isLoading: proceduresLoading } =
    useFetchProceduresQuery({ page: "1", perPage: "10", search: "" });
  const { data: patientsData, isLoading: patientsLoading } =
    useFetchPatientsQuery({
      page: "1",
      per_page: 10,
      search: "",
    });
  const { data: employeesData, isLoading: employeesLoading } =
    useFetchEmployeesNoPaginateQuery();

  const [localProcedures, setLocalProcedures] = useState<Procedure[]>(
    draft.procedures?.map((proc) => proc) || []
  );
  const [editingProcedure, setEditingProcedure] = useState<boolean>(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [selectedPatient, setSelectedPatient] = useState<Option | undefined>();
  const [selectedProcedure, setSelectedProcedure] = useState<
    Option | undefined
  >();
  const [showCancelDialog, setShowCancelDialog] = useState<boolean>(false);

  const initialValues = {
    patient_id: patientId || draft.patient_id || "",
    observations: draft.observations || "",
    voucherCode: draft.voucherCode || "",
    status: "pending",
    procedures: draft.procedures || [
      {
        id: "",
        quantity: 1,
        value: 0,
        employee_id: "",
      },
    ],
  };

  const formik = useFormik({
    initialValues,
    validationSchema: BudgetValidationSchema(),
    onSubmit: async (values, { resetForm }) => {
      mutateAsync(values, {
        onSuccess: () => {
          handleClose();
          resetForm();
          clearDraft();
          toast({
            title: t("LABEL.success"),
            description: t("MESSAGE.created successfully"),
          });
        },
        onError: (error) => {
          toast({
            title: t("LABEL.error"),
            description: DisplayError(error, t("MESSAGE.error_message"), t),
            variant: "destructive",
          });
        },
      });
    },
  });

  const formikRequestProcedure = useFormik({
    initialValues: {
      id: "",
      quantity: 1,
      value: 0,
      employee_id: "",
    },
    validationSchema: BudgetRequestProcedureValidationSchema(),
    onSubmit: async (values) => {
      const procedure = searchedProcedures.current.get(values.id) ?? proceduresData?.items.find(
        (proc) => proc.id === values.id
      );
      const employee = employeesData?.find(
        (emp) => emp.id === values.employee_id
      );

      let updatedProcedures;

      if (editIndex !== null) {
        updatedProcedures = localProcedures.map((item, index) =>
          index === editIndex
            ? {
              ...values,
              procedureName: procedure?.name || (selectedProcedure?.value === values.id ? String(selectedProcedure.label) : "Desconhecido"),
              employeeName: employee?.person.name || "Desconhecido",
            }
            : item
        );
        setEditIndex(null);
      } else {
        updatedProcedures = [
          ...localProcedures,
          {
            ...values,
            procedureName: procedure?.name || (selectedProcedure?.value === values.id ? String(selectedProcedure.label) : "Desconhecido"),
            employeeName: employee?.person.name || "Desconhecido",
          },
        ];
      }

      setLocalProcedures(updatedProcedures);

      const sanitizedProcedures = updatedProcedures.map((item) => ({
        id: item.id,
        quantity: item.quantity,
        value: item.value,
        employee_id: item.employee_id,
      }));

      formik.setFieldValue("procedures", sanitizedProcedures);

      reset();
      clearDraft();
    },
  });

  const handleEdit = (index: number) => {
    const procedureToEdit = localProcedures[index];
    formikRequestProcedure.setValues({
      id: procedureToEdit.id,
      quantity: procedureToEdit.quantity,
      value: procedureToEdit.value,
      employee_id: procedureToEdit.employee_id,
    });
    setSelectedProcedure({ value: procedureToEdit.id, label: procedureToEdit.procedureName });
    setEditIndex(index);
    setEditingProcedure(true);
  };

  const handleDelete = (index: number) => {
    const remaining = localProcedures.filter((_, i) => i !== index);
    setLocalProcedures(remaining);
    formik.setFieldValue("procedures", remaining.map(({ id, quantity, value, employee_id }) => ({ id, quantity, value, employee_id })));
  };

  const reset = () => {
    setSelectedProcedure(undefined);
    setEditingProcedure(false);
    formikRequestProcedure.setFieldValue("value", 0);
    formikRequestProcedure.resetForm();
    setEditIndex(null);
  };

  function handleSaveDraftAndClose() {
    const {
      patient_id,
      observations,
      voucherCode,
      procedures: mainProcedures,
    } = formik.values;

    const hasFilledMainFormFields =
      !!patient_id ||
      !!observations ||
      !!voucherCode ||
      mainProcedures.length > 0;

    const hasValidProcedures =
      localProcedures.length > 0 &&
      localProcedures.every((proc) => proc.value > 0);

    if (!hasFilledMainFormFields && !hasValidProcedures) {
      handleClose();
      return;
    }

    const draftData: DraftProps = {
      patient_id,
      observations,
      voucherCode,
      procedures: localProcedures.map((proc) => ({
        id: proc.id,
        quantity: proc.quantity,
        value: proc.value,
        employee_id: proc.employee_id,
        procedureName: proc.procedureName,
        employeeName: proc.employeeName,
      })),
    };

    setDraft(draftData);
    handleClose();
    toast({
      title: "Rascunho",
      description: "Rascunho salvo com sucesso!",
      variant: "info",
    });
  }

  function handleCancelButtonClick() {
    const {
      patient_id,
      observations,
      voucherCode,
      procedures: mainProcedures,
    } = formik.values;

    const hasFilledMainFormFields =
      !!patient_id ||
      !!observations ||
      !!voucherCode ||
      mainProcedures.length > 0;

    const hasValidProcedures =
      localProcedures.length > 0 &&
      localProcedures.every((proc) => proc.value > 0);

    // Se não há dados para salvar como rascunho, apenas feche o formulário
    if (!hasFilledMainFormFields && !hasValidProcedures) {
      handleClose();
      return;
    }

    setShowCancelDialog(true);
  }

  // const validateProcedures = (procedures: any[]) => {
  //   if (!procedures || procedures.length === 0) {
  //     return "Pelo menos um procedimento é obrigatório";
  //   }

  //   for (const proc of procedures) {
  //     if (!proc.id) return "Procedimento é obrigatório";
  //     if (!proc.employee_id) return "Profissional é obrigatório";
  //     if (!proc.value || proc.value <= 0) return "Valor inválido";
  //     if (!proc.quantity || proc.quantity < 1) return "Quantidade inválida";
  //   }

  //   return undefined;
  // };

  function handleJustCancel() {
    clearDraft();
    handleClose();
  }

  function calculateTotal() {
    return localProcedures.reduce(
      (total, procedure) => total + procedure.value * procedure.quantity,
      0
    );
  }

  const handleSearchPatients = useCallback(async (term: string) => {
    try {
      const { data } = await httpRequest.get<PatientsResponse>(`/patients`, {
        params: { search: term },
      });
      queryClient.setQueryData(["patients", { search: term }], data);
      data.items.forEach(patient => searchedPatients.current.set(patient.id, { value: patient.id, label: patient.person.name }));

      return (
        data?.items.map((patient) => ({
          value: patient.id,
          label: patient.person.name,
        })) || []
      );
    } catch {
      return [];
    }
  }, [queryClient]);

  const handleSearchProcedures = useCallback(async (term: string) => {
    try {
      const { data } = await httpRequest.get<ProcedureResponse>(`/procedures`, {
        params: { search: term },
      });
      queryClient.setQueryData(["procedures", { search: term }], data);
      data.items.forEach(procedure => searchedProcedures.current.set(procedure.id, procedure));

      return (
        data?.items.map((procedure) => ({
          value: procedure.id,
          label: procedure.name,
        })) || []
      );
    } catch {
      return [];
    }
  }, [queryClient]);

  useQuery({
    queryKey: ["patient", formik.values.patient_id],
    queryFn: async () => {
      if (!formik.values.patient_id) return null;
      const { data } = await httpRequest.get(
        `/patients/${formik.values.patient_id}`
      );
      return data;
    },
    enabled: !!formik.values.patient_id && selectedPatient?.value !== formik.values.patient_id,
    onSuccess: (data) => {
      if (data?.id === formik.values.patient_id) {
        setSelectedPatient({
          value: data.id,
          label: data.person.name,
        });
      }
    },
  });

  useQuery({
    queryKey: ["procedures", formikRequestProcedure.values.id],
    queryFn: async () => {
      if (!formikRequestProcedure.values.id) return null;
      const { data } = await httpRequest.get(
        `/procedures/${formikRequestProcedure.values.id}`
      );
      return data;
    },
    enabled: !!formikRequestProcedure.values.id && selectedProcedure?.value !== formikRequestProcedure.values.id,
    onSuccess: (data) => {
      if (data?.id === formikRequestProcedure.values.id) {
        setSelectedProcedure({
          value: data.id,
          label: data.name,
        });
      }
    },
  });

  return (
    <>
      <ScrollArea className="h-full 2xl:h-[90vh] 2xl:pb-10">
        <section className="space-y-5">
          <form
            onSubmit={formikRequestProcedure.handleSubmit}
            className="flex flex-col items-center justify-center gap-2"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 w-full">
              <div className="space-y-1">
                <Label>{t("LABEL.procedure")}</Label>
                <MultiSelect
                  options={
                    proceduresData?.items.map((procedure) => ({
                      value: procedure.id,
                      label: procedure.name,
                    })) || []
                  }
                  loading={proceduresLoading}
                  isInvalid={
                    formikRequestProcedure.touched.id &&
                    formikRequestProcedure.errors.id
                  }
                  isValid={
                    formikRequestProcedure.touched.id &&
                    !formikRequestProcedure.errors.id
                  }
                  placeholder="Selecione um procedimento..."
                  emptyMessage="Nenhum procedimento encontrado"
                  selectedOptions={selectedProcedure?.value === formikRequestProcedure.values.id ? [selectedProcedure] : []}
                  value={formikRequestProcedure.values.id}
                  onChange={(value) => {
                    formikRequestProcedure.setFieldValue("id", value);
                    const selected = searchedProcedures.current.get(String(value)) ?? proceduresData?.items.find(p => p.id === value);
                    formikRequestProcedure.setFieldValue("value", selected ? Number(selected.value) : 0);
                    setSelectedProcedure(undefined);
                    if (selected) {
                      setSelectedProcedure({
                        value: selected.id,
                        label: selected.name,
                      });
                    }
                  }}
                  onSearch={handleSearchProcedures}
                  onBlur={() => formikRequestProcedure.handleBlur("id")}
                  messageError={formikRequestProcedure.errors.id}
                />
              </div>
              <div className="space-y-1">
                <Label>{t("LABEL.collaborator")}</Label>
                <MultiSelect
                  options={
                    employeesData?.map((employee) => ({
                      value: employee.id,
                      label: employee.person.name,
                    })) || []
                  }
                  loading={employeesLoading}
                  isInvalid={
                    formikRequestProcedure.touched.employee_id &&
                    formikRequestProcedure.errors.employee_id
                  }
                  isValid={
                    formikRequestProcedure.touched.employee_id &&
                    !formikRequestProcedure.errors.employee_id
                  }
                  placeholder="Selecione um profissional..."
                  emptyMessage="Nenhum profissional encontrado"
                  value={formikRequestProcedure.values.employee_id}
                  onChange={(value) =>
                    formikRequestProcedure.setFieldValue("employee_id", value)
                  }
                  onBlur={() =>
                    formikRequestProcedure.handleBlur("employee_id")
                  }
                  messageError={formikRequestProcedure.errors.employee_id}
                />
              </div>
              <div className="space-y-1">
                <Label>Nº de sessões</Label>
                <Input
                  isInvalid={
                    formikRequestProcedure.touched.quantity &&
                    formikRequestProcedure.errors.quantity
                  }
                  isValid={
                    formikRequestProcedure.touched.quantity &&
                    !formikRequestProcedure.errors.quantity
                  }
                  {...formikRequestProcedure.getFieldProps("quantity")}
                  messageError={formikRequestProcedure.errors.quantity}
                />
              </div>
              <div className="space-y-1">
                <Label>Valor por sessão</Label>
                <MoneyInput
                  isInvalid={
                    formikRequestProcedure.touched.value &&
                    formikRequestProcedure.errors.value
                  }
                  defaultValue={formikRequestProcedure.values.value}
                  isValid={
                    formikRequestProcedure.touched.value &&
                    !formikRequestProcedure.errors.value
                  }
                  {...formikRequestProcedure.getFieldProps("value")}
                  messageError={formikRequestProcedure.errors.value}
                />
              </div>
            </div>
            <div className="flex items-center justify-center gap-x-2">
              <Button className="px-10">
                {t("LABEL.add")} <ArrowDown className="size-4" />
              </Button>
              {editingProcedure && (
                <Button variant="secondary" onClick={reset}>
                  {t("LABEL.cancel")}
                </Button>
              )}
            </div>
          </form>
          <div className="flex items-center justify-end gap-3 my-3">
            {/* <Button variant="secondary">{t("LABEL.export excel")}</Button> */}
            {/* <Button variant="secondary">{t("LABEL.export pdf")}</Button> */}
          </div>
          <div
            data-hasError={!!formik.errors.procedures}
            className="border data-[hasError=true]:border-red-600 rounded-md overflow-hidden"
          >
            <div className="relative">
              <Table>
                {localProcedures && localProcedures.length <= 0 && (
                  <TableCaption className="pb-5">
                    {t("LABEL.no results")}
                  </TableCaption>
                )}
                <TableHeader className="sticky top-0 z-10 bg-zinc-100">
                  <TableRow>
                    <TableHead className="text-zinc-500 font-semibold">
                      {t("LABEL.procedure")}
                    </TableHead>
                    <TableHead className="text-zinc-500 font-semibold text-end" />
                  </TableRow>
                </TableHeader>
              </Table>
              <ScrollArea className="max-h-[180px] 2xl:max-h-[280px] h-full overflow-y-scroll bg-white">
                <Table>
                  <TableBody className="px-0">
                    {localProcedures.map((procedure, index) => (
                      <TableRow
                        key={index}
                        data-editing={
                          formikRequestProcedure.values.id &&
                          formikRequestProcedure.values.id === procedure.id
                        }
                        className="border-b data-[editing=true]:bg-teal-100 px-0"
                      >
                        <TableCell colSpan={1}>
                          <div className="space-y-2">
                            <h4 className="text-base text-zinc-600 font-semibold">
                              {procedure.procedureName}
                            </h4>
                            <p className="text-sm text-zinc-500 font-medium">
                              {procedure.quantity} Sessões
                            </p>
                            <p className="text-sm text-zinc-500 font-medium">
                              {procedure.employeeName}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell colSpan={2}>
                          <div className="flex items-center justify-end gap-20">
                            <h4 className="text-base text-zinc-600 font-bold">
                              {formatValue(
                                Number(procedure.value) * procedure.quantity
                              )}
                            </h4>
                            <div className="flex justify-end items-center gap-2">
                              <button
                                aria-label={`Editar procedimento ${procedure.procedureName}`}
                                onClick={() => handleEdit(index)}
                                className="p-2 rounded bg-zinc-300 hover:bg-zinc-400/50 transition-all text-zinc-600 text-base"
                              >
                                <Pencil className="size-4" />
                              </button>
                              <button
                                aria-label={`Remover procedimento ${procedure.procedureName}`}
                                onClick={() => handleDelete(index)}
                                className="p-2 rounded bg-red-500 hover:bg-red-600 transition-all text-white text-base"
                              >
                                <Trash className="size-4" />
                              </button>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          </div>
          <div className="text-base text-teal-700 flex items-center justify-between font-semibold">
            <h3>Total</h3>
            <h3 className="pr-2">{formatValue(calculateTotal())}</h3>
          </div>
          <form onSubmit={formik.handleSubmit} className="space-y-1">
            <div className="space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 w-full">
                <div className="space-y-1">
                  <Label>{t("LABEL.patient")}</Label>
                  <MultiSelect
                    options={
                      patientsData?.items.map((patient) => ({
                        value: patient.id,
                        label: patient.person.name,
                      })) || []
                    }
                    loading={patientsLoading}
                    isInvalid={
                      formik.touched.patient_id && formik.errors.patient_id
                    }
                    isValid={
                      formik.touched.patient_id && !formik.errors.patient_id
                    }
                    placeholder="Selecione um paciente..."
                    emptyMessage="Nenhum paciente encontrado"
                    selectedOptions={selectedPatient?.value === formik.values.patient_id ? [selectedPatient] : []}
                    value={formik.values.patient_id}
                    onChange={(value) => {
                      formik.setFieldValue("patient_id", value);
                      const selected = patientsData?.items.find(p => p.id === value);
                      setSelectedPatient(searchedPatients.current.get(String(value)) ?? (selected ? {
                        value: selected.id, label: selected.person.name,
                      } : undefined));
                    }}
                    onSearch={handleSearchPatients}
                    onBlur={() => formik.handleBlur("patient_id")}
                    messageError={formik.errors.patient_id}
                    showError={
                      formik.touched.patient_id && !formik.errors.patient_id
                    }
                    disabled={!!patientId}
                  />
                </div>

                <div className="space-y-1">
                  <Label>Inserir voucher</Label>
                  <div className="flex gap-1">
                    <Input
                      isInvalid={
                        formik.touched.voucherCode && formik.errors.voucherCode
                      }
                      isValid={
                        formik.touched.voucherCode && !formik.errors.voucherCode
                      }
                      {...formik.getFieldProps("voucherCode")}
                      messageError={formik.errors.voucherCode}
                      showError={
                        formik.touched.voucherCode && !formik.errors.voucherCode
                      }
                    />
                    {/* <Button className="bg-zinc-100 border border-zinc-200 text-zinc-600 hover:bg-zinc-50">
                      {t("LABEL.apply")}
                    </Button> */}
                  </div>
                </div>
              </div>
              <div className="space-y-1">
                <Label>{t("LABEL.observation")}</Label>
                <Textarea className="resize-none" {...formik.getFieldProps("observations")} messageError={formik.errors.patient_id} />
              </div>
            </div>
            <div className="flex items-center gap-x-2">
              <Button isLoading={isLoading} className="px-10">
                {t("LABEL.save")}
              </Button>
              <Button
                type="button"
                onClick={handleCancelButtonClick}
                className="px-10"
                variant="secondary"
              >
                {t("LABEL.cancel")}
              </Button>
            </div>
          </form>
        </section>
      </ScrollArea>
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar orçamento</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-zinc-600">
            <p>Deseja salvar este orçamento como rascunho antes de sair?</p>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={handleJustCancel}>
              Apenas cancelar
            </Button>
            <Button onClick={handleSaveDraftAndClose}>
              Salvar como rascunho
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
